import {
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  actionsKeys,
  cn,
  formatAmount,
  getRequiredFields,
  invoiceActionStatus,
  invoiceParkStatus,
  toApiSort,
} from "@/lib/utils";
import { RootState } from "@/redux/store";
import { getOwnerBomBase, routes } from "@/routes/utils";
import {
  useGetParkDocumentList,
  useGetPostedDocumentList,
  useReversePostedDocMutation,
} from "@/services";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { formValues, schema } from "./validations";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  parkDocumentSearch,
  postDocumentSearch,
  SEARCH_CRITERIA,
  SEARCH_TYPE,
  searchType,
} from "./consts";
import { MoreVertical, Undo2 } from "lucide-react";
import { toast } from "@/components/Toast";
import { useQueryClient } from "@tanstack/react-query";
import { setAllInvoiceStatus, setInvoiceIds, setPaymentIds } from "@/redux/slices";

const useInvoiceListing = () => {
  const { invoiceAction } = useParams();
  const isPosted = invoiceAction == "posted";
  const location = useLocation();
  const fromDashboard = location.pathname?.includes("/dashboard");
  const base = getOwnerBomBase(location.pathname);
  const [globalFilter, setGlobalFilter] = useState<string>("");
  const [pageNumber, setPageNumber] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(10);
  const [sorting, setSorting] = useState([{ id: "createdAt", desc: true }]);
  const [openReverseDialog, setOpenReverseDialog] = useState<boolean>(false);
  const [reverseComment, setReverseComment] = useState<string>("");
  const [reversedRow, setReversedRow] = useState<any>(null);
  const [showLinkPopup, setShowLinkPopup] = useState<boolean>(false);
  const [clearingId, setClearingId] = useState<string>("");
  const [reversalId, setReversalId] = useState<string>("");
  const dispatch = useDispatch();
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const form = useForm<formValues>({
    defaultValues: {
      [SEARCH_TYPE]: "",
      [SEARCH_CRITERIA]: "",
    },
    resolver: zodResolver(schema),
  });
  const requiredFields = getRequiredFields(schema);

  const {
    communityData: communityDetail,
    currentFinanceYear,
    loggedInUserData,
    invoiceIds,
    invoiceFrom,
    vendors,
    invoiceDueDate, accessPermission
  } = useSelector((state: RootState) => state.currentData);

  const {
    data: parkedDocumentList,
    isLoading: isParkedDocLoading,
    refetch: refetchParkedDoc,
    isRefetching: isParkedDocRefetching,
  } = useGetParkDocumentList({
    data: {
      filters: [
        {
          field: "year",
          value: currentFinanceYear,
          operator: "EQUAL",
        },
        {
          field: "communityId",
          value: communityDetail?.communityId,
          operator: "EQUAL",
        },
        {
          field: "documentType",
          value: "VI",
          operator: "EQUAL",
        },
        {
          field: "sourceSystem",
          value: "MANUAL_JE",
          operator: "NOT_EQUAL",
        },
        ...(form.watch()[SEARCH_TYPE]
          ? [
              {
                field: form.watch()[SEARCH_TYPE],
                value: form.watch()[SEARCH_CRITERIA],
                operator:
                  (
                    [searchType, parkDocumentSearch]?.find(
                      (data: any) =>
                        data.value == (form.watch()[SEARCH_TYPE] as string)
                    ) as any
                  )?.operator || "EQUAL",
              },
            ]
          : []),
        ...(invoiceAction == invoiceActionStatus.pending_for_cam_approval ||
        invoiceAction == invoiceActionStatus.pending_for_agm_approval ||
        invoiceAction == invoiceActionStatus.pending_for_board_approval ||
        invoiceAction == invoiceActionStatus.pending_for_my_approval ||
        invoiceAction == invoiceActionStatus.pending_for_approval
          ? [
              {
                field: "approvalDetails",
                value:
                  invoiceAction ==
                    invoiceActionStatus.pending_for_agm_approval ||
                  invoiceAction ==
                    invoiceActionStatus.pending_for_board_approval
                    ? `${invoiceParkStatus[
                        invoiceAction
                      ]?.approvalStatus?.toUpperCase()}`
                    : invoiceAction ==
                    invoiceActionStatus.pending_for_approval ? `CAM:PENDING_APPROVAL` : `${loggedInUserData?.userId}:${invoiceParkStatus[
                        invoiceAction
                      ]?.approvalStatus?.toUpperCase()}`,
                operator: invoiceAction ==
                invoiceActionStatus.pending_for_approval ? "NOT_CONTAINS" : "CONTAINS",
              },
              {
                field: "status",
                value: invoiceParkStatus[invoiceAction]?.status?.toUpperCase(),
                operator: "CONTAINS",
              },
            ]
          : invoiceAction != "all"
          ? [
              {
                field: "status",
                value:
                  invoiceParkStatus[
                    invoiceAction as any
                  ]?.status?.toUpperCase(),
                operator: "EQUAL",
              },
            ]
          : []),
      ],
      pageNumber,
      pageSize,
      sort: toApiSort(sorting, { field: "createdAt", direction: "DESC" }),
    },
    queryParams: {
      enabled: invoiceAction != "posted",
    },
  });

  const {
    data: postedDocumentList,
    isLoading: isPostedDocLoading,
    refetch: refetchPostedDoc,
    isRefetching: isPostedDocRefetching,
  } = useGetPostedDocumentList({
    data: {
      filters: [
        {
          field: "year",
          value: currentFinanceYear,
          operator: "EQUAL",
        },
        {
          field: "communityId",
          value: communityDetail?.communityId,
          operator: "EQUAL",
        },
        {
          field: "documentType",
          value: ["VI", "VX"],
          operator: "CONTAINS_ANY",
        },
        ...(fromDashboard
          ? vendors
            ? [
                {
                  field: "vendorName",
                  value: vendors,
                  operator: "CONTAINS_ANY",
                },
              ]
            : invoiceDueDate == "overdue"
            ? [
                {
                  field: "dueDate",
                  value: moment(),
                  operator: "LESS_THAN",
                },
                {
                  field: "clearingId",
                  value: "",
                  operator: "NULL",
                },
              ]
            : invoiceDueDate == "7_days_due"
            ? [
                {
                  field: "dueDate",
                  value: [moment(), moment().add(7, "days")],
                  operator: "BETWEEN",
                },
                {
                  field: "clearingId",
                  value: "",
                  operator: "NULL",
                },
              ]
            : []
          : []),
        ...(form.watch()[SEARCH_TYPE]
          ? [
              {
                field: form.watch()[SEARCH_TYPE],
                value: form.watch()[SEARCH_CRITERIA],
                operator:
                  (
                    [searchType, postDocumentSearch]?.find(
                      (data: any) =>
                        data.value == (form.watch()[SEARCH_TYPE] as string)
                    ) as any
                  )?.operator || "EQUAL",
              },
            ]
          : []),
        ...(invoiceAction != "all"
          ? [
              {
                field: "status",
                value: invoiceAction?.toUpperCase(),
                operator: "EQUAL",
              },
            ]
          : []),
        ...(invoiceFrom == "payment-listing" && invoiceIds
          ? [
              {
                field: "postDocumentHeaderId",
                value: invoiceIds?.split(","),
                operator: "CONTAINS_ANY",
              },
            ]
          : []),
      ],
      pageNumber,
      pageSize,
      sort: toApiSort(sorting, { field: "createdAt", direction: "DESC" }),
    },
    queryParams: {
      enabled: isPosted,
    },
  });

  const refetchDoc = isPosted ? refetchPostedDoc : refetchParkedDoc;
  const isDocLoading = isPosted ? isPostedDocLoading : isParkedDocLoading;
  const documentList = isPosted ? postedDocumentList : parkedDocumentList;
  const isDocRefetching = isPosted
    ? isPostedDocRefetching
    : isParkedDocRefetching;

  useEffect(() => {
    refetchDoc();
  }, [pageNumber, pageSize, sorting]);

  useEffect(() => {
    if (globalFilter?.length >= 2 || globalFilter?.length == 0) {
      refetchDoc();
    }
  }, [globalFilter]);

  const onSubmit = () => {
    refetchDoc();
  };

  const onClear = () => {
    form.reset();
    setTimeout(() => refetchDoc(), 100);
  };

  const breadcrumbDataInvoice = useMemo(
    () => [
      {
        link: `/${routes.bom}/${routes.finance}`,
        name: `Activity Dashboard`,
      },
      {
        link: `/${routes.bom}/${routes.finance}/${routes.invoice}`,
        name: "Invoice",
      },
      {
        link: "",
        name: (
          <span className="text-primary">
            VI
            {invoiceParkStatus[invoiceAction || ""]
              ? " - " + invoiceParkStatus[invoiceAction || ""]?.label
              : ""}
          </span>
        ),
      },
    ],
    []
  );

  const breadcrumbDataDashboard = useMemo(
    () => [
      {
        link: `/${base}/${routes.dashboard}`,
        name: `Dashboard`,
      },
      {
        link: "",
        name: (
          <span className="text-primary">
            VI
            {invoiceParkStatus[invoiceAction || ""]
              ? " - " + invoiceParkStatus[invoiceAction || ""]?.label
              : ""}
          </span>
        ),
      },
    ],
    []
  );

  const breadcrumbData = fromDashboard
    ? breadcrumbDataDashboard
    : breadcrumbDataInvoice;

  const onBackToPaymentList = () => {
    dispatch(setInvoiceIds(null));
    dispatch(setPaymentIds(null));
    navigate(`/${routes.bom}/${routes.finance}/${routes.payment}/post`);
  };

  const onBackToDashboard = () => {
    navigate(`/${base}/${routes.dashboard}`);
  };

  const postedColumns = [
    {
      accessorKey: "documentNumber",
      header: (
        <div>
          Posted <br /> Doc. ID
        </div>
      ),
      accessorFn: (row: any) => String(row.documentNumber || ""),
      size: 50,
    },
    {
      accessorKey: "parkDocumentHeaderId",
      header: (
        <div>
          Parked <br /> Doc. ID
        </div>
      ),
      accessorFn: (row: any) => String(row.parkDocumentHeaderId || ""),
      size: 50,
    },
    {
      accessorKey: "reversalPostDocumentHeaderId",
      header: (
        <div>
          Reversal <br /> Doc. ID
        </div>
      ),
      accessorFn: (row: any) => String(row.reversalPostDocumentHeaderId || ""),
      size: 50,
    },
  ];

  const postedColumnsAction = [
    {
      id: "linked_document",
      header: "Linked Document",
      cell: (row: any) => {
        return (
          <Button
            variant="link"
            type="button"
            onClick={(e: any) => {
              e.preventDefault();
              e.stopPropagation();
              setShowLinkPopup(true);
              setClearingId(row?.row?.original?.paymentDocumentHeaderId);
              setReversalId(row?.row?.original?.reversalPostDocumentHeaderId);
            }}
          >
            View
          </Button>
        );
      },
    },
    {
      id: "actions",
      header: "Actions",
      enableSorting: false,
      cell: (row: any) => {
        return (
          <DropdownMenu>
            <DropdownMenuTrigger>
              <MoreVertical className="w-5 h-5 cursor-pointer" />
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="bg-white border-0 shadow-md"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              <DropdownMenuItem
                className="flex items-center gap-2"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setReversedRow(row.row.original);
                  setOpenReverseDialog(true);
                }}
                disabled={
                  row?.row?.original?.reversed_header ||
                  row?.row?.original?.clearingId
                }
              >
                <Undo2 className="w-4 h-4" />
                Reversal
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];

  const commonColumns = [
    {
      accessorKey: "parkDocumentHeaderId",
      header: (
        <div>
          Parked <br /> Doc. ID
        </div>
      ),
      accessorFn: (row: any) => String(row.parkDocumentHeaderId || ""),
      size: 50,
    },
  ];

  // Define Table Columns
  const columns = [
    ...(isPosted ? postedColumns : commonColumns),
    {
      accessorKey: "year",
      header: <div>Year</div>,
      accessorFn: (row: any) => String(row.year),
      size: 80,
    },
    {
      accessorKey: "vendorName",
      header: <div>Vendor Name</div>,
      accessorFn: (row: any) => String(row?.vendorName || ""),
    },
    {
      accessorKey: "invoiceNumber",
      header: <div>Invoice No.</div>,
      accessorFn: (row: any) => String(row?.invoiceNumber) || "",
    },
    {
      accessorKey: "invoiceAmount",
      meta: { align: "right" },
      header: <div>Invoice Amt.</div>,
      accessorFn: (row: any) => formatAmount(row?.invoiceAmount || 0),
    },
    {
      accessorKey: "approvedAmount",
      meta: { align: "right" },
      header: <div>Approved Amt.</div>,
      accessorFn: (row: any) => formatAmount(row?.approvedAmount || 0),
    },
    {
      accessorKey: "status",
      header: <div>Status</div>,
      cell: (row: any) => (
        <Badge
          variant="outline"
          className={cn(
            invoiceParkStatus[row.row.original.status?.toLowerCase()]?.color,
            "rounded-full px-4 py-2"
          )}
        >
          {invoiceParkStatus[row?.row?.original?.status?.toLowerCase()]?.label}
        </Badge>
      ),
    },
    {
      accessorKey: "submittedOn",
      header: <div>Submitted On</div>,
      accessorFn: (row: any) =>
        moment(row?.submittedOn || "").format("MM/DD/YYYY"),
    },
    ...(isPosted ? postedColumnsAction : []),
  ];

  const invoicePermission = accessPermission[actionsKeys?.INVOICE?.toLowerCase()];

  const onRowClick = (row: any) => {
    dispatch(setAllInvoiceStatus(""));
    if (isPosted) {
      navigate(`${row.original.postDocumentHeaderId}/view`);
    } else if (row.original.status?.toLowerCase() == "posted") {
      dispatch(setAllInvoiceStatus("posted"));
      navigate(`${row.original.postDocumentHeaderId}/view`);
    } else {
      let action = "view";
      if (row.original.status?.toLowerCase() == "draft") {
        if (
          invoicePermission["UPDATE"] ||
          invoicePermission["SUBMIT"] ||
          invoicePermission["APPROVE"]
        ) {
          action = "edit";
        }
      }
      navigate(`${row.original.parkDocumentHeaderId}/${action}`);
    }
  };

  const { mutate: reversePostedDoc, isPending: isReversingPostedDoc } =
    useReversePostedDocMutation({
      onError: (error?: any) => {
        toast.error(
          typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong"
        );
        return error?.response?.data?.message;
      },
      onSuccess: () => {
        setOpenReverseDialog(false);
        setReverseComment("");
        setReversedRow(null);
        queryClient.invalidateQueries({
          queryKey: ["posted-documents"],
        });
        toast.success("Reversal Successfully");
      },
    });

  const onReverseInvoice = () => {
    if (reverseComment?.trim()?.length < 5) {
      toast.error("Please enter a comment with at least 5 characters");
      return;
    }
    reversePostedDoc({
      data: {
        postedDocumentHeaderId: reversedRow?.postDocumentHeaderId,
        reversalComment: reverseComment,
      },
    });
  };

  const onCommentChange = (e: any) => {
    setReverseComment(e.target.value);
  };

  const viewPaymentDoc = () => {
    if (clearingId) {
      navigate(
        `/${routes.bom}/${routes.finance}/${routes.payment}/all/${clearingId}/view`,
        {
          state: { from: "invoice-listing" },
        }
      );
    }
  };

  const viewReversalDoc = () => {
    if (reversalId) {
      navigate(
        `/${routes.bom}/${routes.finance}/${routes.invoice}/posted/${reversalId}/view`,
        {
          state: { from: "invoice-listing" },
        }
      );
    }
  };

  const isFromPaymentListing = invoiceFrom == "payment-listing";

  const onBackClick = () => {
    return fromDashboard
      ? onBackToDashboard()
      : isFromPaymentListing
      ? onBackToPaymentList()
      : navigate(`/${routes.bom}/${routes.finance}/${routes.invoice}`);
  };

  return {
    breadcrumbData,
    columns,
    globalFilter,
    setGlobalFilter,
    isMobile,
    onRowClick,
    invoiceAction,
    isDocLoading,
    documentList,
    isDocRefetching,
    setPageNumber,
    setPageSize,
    pageNumber,
    pageSize,
    sorting,
    setSorting,
    form,
    requiredFields,
    onSubmit,
    onClear,
    openReverseDialog,
    setOpenReverseDialog,
    onReverseInvoice,
    onCommentChange,
    isPosted,
    isReversingPostedDoc,
    showLinkPopup,
    setShowLinkPopup,
    viewPaymentDoc,
    clearingId,
    viewReversalDoc,
    reversalId,
    onBackToPaymentList,
    isFromPaymentListing,
    fromDashboard,
    onBackToDashboard,
    onBackClick,
    currentFinanceYear
  };
};

export default useInvoiceListing;
